import { motion } from "framer-motion";
import { Reveal } from "./Reveal";

const PTS = [[60, 220], [140, 70], [300, 50], [360, 200], [230, 260], [180, 160], [260, 150]];
const ROWS = PTS.map(([x, y], i) => `${101 + i}, ${(5000 + y * 1.37).toFixed(2)}, ${(2200 + x * 1.21).toFixed(2)}, ${(99 + i * 0.41).toFixed(2)}, ${i < 5 ? "IP" : "SHOT"}`);

// Survey point file transforming into CAD linework (decorative, reversible on scroll)
export const PointToCad = () => (
  <div className="relative border border-[#102A43]/12 bg-[#0E2438] grid-dark text-white" data-testid="point-to-cad-visual">
    <div className="flex items-center justify-between border-b border-white/10 px-4 py-3 font-mono text-[11px] tracking-widest text-[#9fb3c8]">
      <span>JOB_2214.CSV → C-PROP.DWG</span><span className="text-[#D8B66A]">PNEZD</span>
    </div>
    <div className="grid md:grid-cols-[1fr_1.4fr]">
      <div className="border-b md:border-b-0 md:border-r border-white/10 p-4 font-mono text-[11px] leading-6 text-[#BFD7EC]">
        {ROWS.map((r, i) => (
          <motion.p key={r} initial={{ opacity: 0.35, x: -6 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ amount: 0.3 }} transition={{ delay: i * 0.08 }}>{r}</motion.p>
        ))}
      </div>
      <svg viewBox="0 0 420 300" className="w-full" fill="none" aria-hidden="true">
        {PTS.map(([x, y], i) => (
          <motion.g key={i} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ amount: 0.3 }} transition={{ delay: 0.2 + i * 0.08 }}>
            <path d={`M${x - 6} ${y}h12M${x} ${y - 6}v12`} stroke={i < 5 ? "#FFFFFF" : "#D8B66A"} />
            <text x={x + 6} y={y - 6} fontSize="9" fill="#9fb3c8" fontFamily="JetBrains Mono">{101 + i}</text>
          </motion.g>
        ))}
        <motion.path d={`M${PTS.slice(0, 5).map((p) => p.join(" ")).join(" L")} Z`} stroke="#5AA9E6" strokeWidth="2"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ amount: 0.3 }} transition={{ duration: 1.8, delay: 0.8, ease: "easeInOut" }} />
        <motion.path d="M180 160 L260 150" stroke="#D8B66A" strokeDasharray="4 4"
          initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ amount: 0.3 }} transition={{ duration: 0.8, delay: 2.4 }} />
        <motion.text x="150" y="40" fontSize="9" fill="#BFD7EC" fontFamily="JetBrains Mono" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ amount: 0.3 }} transition={{ delay: 2.2 }} transform="rotate(-7 150 40)">N 82°51' E 160.4'</motion.text>
      </svg>
    </div>
  </div>
);

const FLOW = [
  ["Field Survey Data", "Supplied points, notes and records"],
  ["Coordinate Processing", "Checks, codes and point structure"],
  ["CAD Drafting", "Linework, symbols and annotation"],
  ["Quality Check", "Independent review against the data"],
  ["Final Deliverable", "DWG, DXF, PDF as agreed"],
];

const Mini = ({ i }) => (
  <svg viewBox="0 0 120 80" className="h-20 w-full" fill="none" stroke="#5AA9E6" aria-hidden="true">
    {i === 0 && [[20, 50], [40, 20], [70, 30], [95, 55], [55, 65], [80, 15]].map(([x, y], k) => <path key={k} d={`M${x - 4} ${y}h8M${x} ${y - 4}v8`} stroke="#D8B66A" />)}
    {i === 1 && [0, 1, 2, 3].map((k) => <path key={k} d={`M14 ${18 + k * 14}h${40 + (k % 2) * 30}`} strokeWidth="4" strokeOpacity=".5" />)}
    {i === 2 && <path d="M15 60 L40 18 L85 22 L105 58 Z M40 18 L65 45 L105 58" strokeWidth="1.6" />}
    {i === 3 && <><rect x="30" y="12" width="60" height="56" /><path d="M44 40l10 10 22-22" stroke="#D8B66A" strokeWidth="2" /></>}
    {i === 4 && <><rect x="20" y="8" width="80" height="64" /><path d="M66 56h30M66 62h30M30 50 L45 22 L70 26 L80 46 Z" /></>}
  </svg>
);

export const ProcessFlow = () => (
  <div className="relative" data-testid="process-flow">
    <motion.div className="absolute left-0 right-0 top-[54px] hidden lg:block h-px bg-[#D8B66A] origin-left" initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ amount: 0.2 }} transition={{ duration: 1.8, ease: "easeInOut" }} />
    <ol className="grid gap-px bg-white/10 lg:bg-transparent lg:gap-6 lg:grid-cols-5">
      {FLOW.map(([t, d], i) => (
        <Reveal as="li" key={t} delay={i * 0.12} className="relative bg-[#102A43] lg:bg-transparent p-6 lg:p-0" data-testid={`process-step-${i}`}>
          <div className="relative z-10 mx-0 grid h-[108px] place-items-center border border-white/15 bg-[#0E2438]"><Mini i={i} /></div>
          <p className="mt-5 font-mono text-[11px] tracking-widest text-[#D8B66A]">STEP 0{i + 1} {i < 4 && <span className="text-[#7f97ad]">→</span>}</p>
          <h3 className="mt-2 font-display text-lg font-bold text-white">{t}</h3>
          <p className="mt-1 text-sm text-[#c3d0dd]">{d}</p>
        </Reveal>
      ))}
    </ol>
  </div>
);
